import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TripDisplay } from "../components/TripDisplay";
import { CollabPanel } from "../components/CollabPanel";
import { Seo } from "../components/Seo";
import { Footer } from "../components/Footer";
import { Compass, Copy, Loader2, Sparkles } from "lucide-react";

export default function SharedTrip() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState("");
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    api
      .get(`/public/trips/${shareId}`)
      .then((res) => setTrip(res.data.trip))
      .catch(() => setError("This trip link is invalid, private, or no longer available."));
  }, [shareId]);

  const copyTrip = async () => {
    if (!user) {
      try { sessionStorage.setItem("copy_share_id", shareId); } catch (e) {}
      toast.info("Sign in (or create a free account) to copy this trip.");
      navigate("/auth");
      return;
    }
    setCopying(true);
    try {
      const { data } = await api.post("/trips/copy", { share_id: shareId });
      toast.success("Copied to your trips!");
      navigate(`/trips/${data.id}?personalize=1`);
    } catch {
      toast.error("Couldn't copy this trip. Please try again.");
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="min-h-screen grain relative">
      <Seo
        title={trip ? `${trip.custom_name || trip.destination} Trip Plan` : "Shared Trip"}
        description={trip ? (trip.tagline || trip.summary || "A trip plan shared via WanderlyMe.") : "A trip plan shared via WanderlyMe."}
        path={`/shared/${shareId}`}
        noindex
      />
      <header className="no-print sticky top-0 z-50 border-b border-black/5 backdrop-blur-xl bg-[#FDFBF7]/80">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Compass className="w-6 h-6 text-[#C8553D]" strokeWidth={1.75} />
            <span className="font-display text-2xl tracking-tight">WanderlyMe</span>
          </Link>
          <Link
            to="/auth"
            className="px-5 py-2 text-sm rounded-full bg-[#1A3B2B] text-[#FDFBF7] hover:bg-[#122a1f] transition-colors"
          >
            Plan your own trip
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {error ? (
          <div className="text-center py-24">
            <p className="font-display text-2xl mb-2">Trip not found</p>
            <p className="text-muted-foreground">{error}</p>
          </div>
        ) : !trip ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
              <p className="text-xs uppercase tracking-widest text-[#C8553D]">Shared with you via WanderlyMe</p>
              <button
                data-testid="copy-trip-btn"
                onClick={copyTrip}
                disabled={copying}
                className="no-print inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C8553D] text-[#FDFBF7] hover:bg-[#a8412f] transition-colors disabled:opacity-60"
              >
                {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                Copy this trip
              </button>
            </div>
            <TripDisplay trip={trip} editable={false} />

            <CollabPanel shareId={shareId} trip={trip} />

            {/* Conversion CTA */}
            <div className="no-print mt-14 rounded-3xl bg-[#1A3B2B] text-[#FDFBF7] p-10 text-center">
              <Sparkles className="w-8 h-8 text-[#C8553D] mx-auto mb-3" />
              <h2 className="font-display text-3xl mb-2">Create your own version with WanderlyMe</h2>
              <p className="opacity-85 max-w-xl mx-auto mb-6">Copy this itinerary and tailor it to your dates, budget, group and interests — our AI personalises the whole plan in seconds.</p>
              <button
                data-testid="cta-copy-trip-btn"
                onClick={copyTrip}
                disabled={copying}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#C8553D] text-[#FDFBF7] hover:bg-[#a8412f] transition-colors disabled:opacity-60"
              >
                {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                Copy &amp; personalise this trip
              </button>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
